import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useAuth } from 'components/context/AuthContext';
import { toast } from 'react-hot-toast';
import { Presentation, Calendar, Loader, Upload, X as XIcon, ChevronDown, Info } from 'lucide-react';
import reportingApi from '../../api/reporting';
import SlideEditorCarousel from './components/SlideEditorCarousel';
import EnhancedDateRangeSelector from 'components/analytics/EnhancedDateRangeSelector';
import EsporaiTooltip from 'components/ui/EsporaiTooltip';
import { useTopBarLeftContent } from 'components/context/TopBarLeftContentContext';
import { formatDate } from 'utils/dateRangeUtils';
import { format, subDays } from 'date-fns';

// Mismo selector de períodos que Analytics20 / AnalyticsStickyFilterBar
const basePeriods = [
  { key: 'TODAY', label: 'Hoy', days: 0, granularity: 'daily' },
  { key: 'YESTERDAY', label: 'Ayer', days: 1, granularity: 'daily' },
  { key: '7D', label: 'Últimos 7 días', days: 7, granularity: 'daily' },
  { key: '14D', label: 'Últimos 14 días', days: 14, granularity: 'daily' },
  { key: '30D', label: 'Últimos 30 días', days: 30, granularity: 'daily' },
  { key: 'ESTE_MES', label: 'Este mes', days: 'current_month', granularity: 'daily' },
  { key: 'MES_PASADO', label: 'El mes pasado', days: 'last_month', granularity: 'daily' },
  { key: 'CUSTOM', label: 'Personalizado', days: null, granularity: 'daily' },
];

const Reporting = () => {
  const { auth } = useAuth();
  const activeBrandId = auth?.user?.active_brand_id;
  const { setBrandSelectorVisibility } = useTopBarLeftContent();

  // Estado de vista: 'config' | 'generating' | 'preview'
  const [viewMode, setViewMode] = useState('config');

  // Configuración del reporte (solo analytics - CombinedAnalyticsService)
  const [reportConfig, setReportConfig] = useState({
    dateRange: {
      start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd')
    },
    brandLogo: null
  });

  // Reporte generado
  const [generatedReport, setGeneratedReport] = useState(null);

  // Selector de período (mismo que Analytics)
  const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);
  const [selectedReportPeriod, setSelectedReportPeriod] = useState('30D');

  const startDateObj = useMemo(() => (reportConfig.dateRange?.start ? new Date(reportConfig.dateRange.start) : null), [reportConfig.dateRange?.start]);
  const endDateObj = useMemo(() => (reportConfig.dateRange?.end ? new Date(reportConfig.dateRange.end) : null), [reportConfig.dateRange?.end]);

  const periodsForModal = useMemo(() => {
    const customLabel =
      selectedReportPeriod === 'CUSTOM' && reportConfig.dateRange?.start && reportConfig.dateRange?.end
        ? `${formatDate(startDateObj, 'full-year-short')} - ${formatDate(endDateObj, 'full-year-short')}`
        : '';
    return basePeriods.map((p) =>
      p.key === 'CUSTOM' ? { ...p, formattedLabel: customLabel } : p
    );
  }, [selectedReportPeriod, reportConfig.dateRange?.start, reportConfig.dateRange?.end, startDateObj, endDateObj]);

  const periodLabel = useMemo(() => {
    if (selectedReportPeriod === 'CUSTOM' && startDateObj && endDateObj) {
      return `${formatDate(startDateObj, 'full-year-short')} - ${formatDate(endDateObj, 'full-year-short')}`;
    }
    const p = basePeriods.find((x) => x.key === selectedReportPeriod);
    return p ? p.label : 'Período';
  }, [selectedReportPeriod, startDateObj, endDateObj]);

  const handleApplyPeriodFilters = useCallback((payload) => {
    if (payload?.range?.startDateStr && payload?.range?.endDateStr) {
      setReportConfig((prev) => ({
        ...prev,
        dateRange: { start: payload.range.startDateStr, end: payload.range.endDateStr },
      }));
      if (payload.periodKey) setSelectedReportPeriod(payload.periodKey);
    }
    setIsPeriodModalOpen(false);
  }, []);

  const validateConfig = () => {
    if (!reportConfig.dateRange.start || !reportConfig.dateRange.end) {
      toast.error('Selecciona un rango de fechas');
      return false;
    }
    return true;
  };
  const handleGenerateReport = async () => {
    try {
      // Validaciones
      if (!validateConfig()) {
        toast.error('Por favor completa los campos requeridos');
        return;
      }

      setViewMode('generating');

      // Llamar API con nombre del reporte (siempre el mismo)
      const response = await reportingApi.generatePPTReport({
        ...reportConfig,
        name: `Reporte ${format(new Date(), 'yyyy-MM-dd')}`,
        template: 'detailed', // Siempre el mismo template
        primaryColor: '#2C3E50', // Siempre el mismo color primario
        secondaryColor: '#34495E', // Siempre el mismo color secundario
        brand_id: activeBrandId
      });

      // response.data contiene la respuesta del backend
      if (response.data && response.data.success) {
        const configWithGenerationDate = {
          ...reportConfig,
          ...(response.data.generationDate
            ? { generationDate: response.data.generationDate }
            : {})
        };

        // Guardar reporte generado
        const reportData = {
          pptx_base64: response.data.pptx_base64,
          slides_data: response.data.slides_data || [],
          filename: response.data.filename,
          total_slides: response.data.total_slides,
          config: configWithGenerationDate
        };

        setGeneratedReport(reportData);

        // Cambiar a vista de preview
        setViewMode('preview');

        toast.success('Reporte generado exitosamente');
      } else {
        throw new Error(response.data?.error || 'Error generando reporte');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(error.message || 'Error al generar el reporte');
      setViewMode('config');
    }
  };

  const handleEditReport = () => {
    setViewMode('config');
  };

  const handleClosePreview = () => {
    setViewMode('config');
    setGeneratedReport(null);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen válida');
      return;
    }

    // Validar tamaño (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 2MB');
      return;
    }

    // Convertir a base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setReportConfig(prev => ({
        ...prev,
        brandLogo: reader.result // base64 string
      }));
      toast.success('Logo cargado exitosamente');
    };
    reader.onerror = () => {
      toast.error('Error al cargar la imagen');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setReportConfig(prev => ({
      ...prev,
      brandLogo: null
    }));
    toast.success('Logo eliminado');
  };

  useEffect(() => {
    setBrandSelectorVisibility(viewMode !== 'preview');
  }, [viewMode, setBrandSelectorVisibility]);

  useEffect(() => {
    return () => {
      setBrandSelectorVisibility(true);
    };
  }, [setBrandSelectorVisibility]);

  // Vista de Preview
  if (viewMode === 'preview' && generatedReport) {
    return (
      <SlideEditorCarousel
        slidesData={generatedReport.slides_data}
        filename={generatedReport.filename}
        pptxBase64={generatedReport.pptx_base64}
        config={generatedReport.config}
        brandId={activeBrandId}
        onEdit={handleEditReport}
        onClose={handleClosePreview}
      />
    );
  }

  // Vista de Generación (Loading)
  if (viewMode === 'generating') {
    return (
      <div className="min-h-screen pt-20 px-4 pb-6">
        <div className="max-w-2xl mx-auto">
          {/* Card principal de carga */}
          <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl p-6 sm:p-8 shadow-2xl">
            {/* Header con info del período */}
            <div className="flex items-center gap-4 mb-8">
              <div className="flex-shrink-0">
                <Loader className="w-10 h-10 text-blue-400 animate-spin" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-white mb-1">Generando Reporte</h2>
                <p className="text-white/70 text-sm">
                  Período: {periodLabel}
                </p>
              </div>
            </div>

            {/* Indicadores de progreso por fase */}
            <div className="space-y-3 mb-8">
              {/* Fase 1: Recopilando datos */}
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-white/90 text-sm font-medium">Recopilando datos de Google Ads y Meta Ads</p>
                </div>
                <span className="text-xs text-green-400">✓</span>
              </div>

              {/* Fase 2: Analizando métricas */}
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0 animate-pulse" />
                <div className="flex-1">
                  <p className="text-white/90 text-sm font-medium">Analizando métricas</p>
                </div>
                <span className="text-xs text-blue-400 animate-pulse">●</span>
              </div>

              {/* Fase 4: Compilando presentación */}
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-white/20 rounded-full flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-white/60 text-sm font-medium">Compilando presentación</p>
                </div>
                <span className="text-xs text-white/40">○</span>
              </div>
            </div>

            {/* Información adicional */}
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <p className="text-xs text-white/60 mb-2">Esto puede tomar de 1 a 5 minutos...</p>
              <div className="w-full bg-white/10 rounded-full h-1 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full animate-pulse"
                  style={{ width: '30%' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Vista de Configuración
  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 sm:pt-24 pb-4 sm:pb-6">
        {/* Formulario de Configuración */}
        <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-500 ease-out">
          <div className="relative z-10 space-y-6 sm:space-y-8">

            {/* Período del Reporte (mismo selector que Analytics20) */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-white mb-3">
                <Calendar size={18} />
                Período del Reporte
              </label>
              <button
                type="button"
                onClick={() => setIsPeriodModalOpen(true)}
                className="flex items-center gap-2 px-4 py-3 w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white/90 hover:text-white rounded-lg text-sm font-medium transition-colors border border-white/20"
              >
                <Calendar className="w-4 h-4 text-white/70" />
                <span className="truncate">{periodLabel}</span>
                <ChevronDown className="w-4 h-4 text-white/70 flex-shrink-0" />
              </button>
              <EnhancedDateRangeSelector
                isOpen={isPeriodModalOpen}
                onClose={() => setIsPeriodModalOpen(false)}
                selectedPeriod={selectedReportPeriod}
                onPeriodChange={setSelectedReportPeriod}
                customStartDate={reportConfig.dateRange?.start || ''}
                customEndDate={reportConfig.dateRange?.end || ''}
                onCustomDatesChange={(start, end) => {
                  setReportConfig((prev) => ({
                    ...prev,
                    dateRange: { start, end },
                  }));
                  setSelectedReportPeriod('CUSTOM');
                }}
                isCompareEnabled={false}
                periods={periodsForModal}
                onApplyFilters={handleApplyPeriodFilters}
              />
            </div>


            <div>
              <div className="flex items-center gap-2 mb-3">
                <label className="block text-sm font-medium text-white">
                  Logo del Cliente (Opcional)
                </label>
                <EsporaiTooltip content="Se mostrará en la portada y pie de página del reporte">
                  <Info className="w-4 h-4 text-blue-400 cursor-help" />
                </EsporaiTooltip>
              </div>

              {!reportConfig.brandLogo ? (
                <label className="flex items-center justify-center gap-2 px-4 py-3 bg-white/10 border border-white/20 border-dashed rounded-lg cursor-pointer hover:bg-white/15 transition-colors">
                  <Upload size={20} className="text-white/70" />
                  <span className="text-sm text-white/90">Cargar logo (PNG, JPG, max 2MB)</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </label>
              ) : (
                <div className="relative bg-white/10 border border-white/20 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <img
                      src={reportConfig.brandLogo}
                      alt="Logo preview"
                      className="h-16 w-16 object-contain bg-white rounded"
                    />
                    <div className="flex-1">
                      <p className="text-sm text-white/90 font-medium">Logo cargado</p>
                      <p className="text-xs text-white/60">Se mostrará en portada y pie de página</p>
                    </div>
                    <button
                      onClick={handleRemoveLogo}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      title="Eliminar logo"
                    >
                      <XIcon size={18} className="text-white/70 hover:text-red-400" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Botón Generar */}
            <div className="pt-6 border-t border-white/20">
              <button
                onClick={handleGenerateReport}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Presentation size={20} />
                Generar Reporte
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reporting;
